import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, SupportedLanguage, TranslationSet } from './translations';

interface LanguageContextProps {
  language: SupportedLanguage;
  t: (keyPath: string, replacers?: Record<string, string | number>) => string;
  setLanguage: (lang: SupportedLanguage) => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('OPTIVELO_LANGUAGE');
      if (saved === 'pt' || saved === 'en' || saved === 'es') {
        return saved;
      }
    }
    return 'pt';
  });

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    localStorage.setItem('OPTIVELO_LANGUAGE', lang);
    // Dispatch a custom event so non-context components or external elements can listen
    window.dispatchEvent(new CustomEvent('optivelo-lang-changed', { detail: lang }));
  };

  // Safe nested keys crawler translation function
  const t = (keyPath: string, replacers?: Record<string, string | number>): string => {
    const keys = keyPath.split('.');
    let current: any = translations[language];

    for (const key of keys) {
      if (current === undefined || current === null) {
        break;
      }
      current = current[key];
    }

    if (typeof current !== 'string') {
      // Fallback to portuguese
      let fallbackCurrent: any = translations['pt'];
      for (const key of keys) {
        if (fallbackCurrent === undefined || fallbackCurrent === null) {
          break;
        }
        fallbackCurrent = fallbackCurrent[key];
      }
      if (typeof fallbackCurrent === 'string') {
        current = fallbackCurrent;
      } else {
        return keyPath; // return raw path if not found
      }
    }

    let translated = current;
    if (replacers) {
      Object.entries(replacers).forEach(([key, val]) => {
        translated = translated.replace(new RegExp(`{${key}}`, 'g'), String(val));
      });
    }

    return translated;
  };

  return (
    <LanguageContext.Provider value={{ language, t, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
